import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { QUERY_FIND_NOTIFICATIONS, QUERY_GET_NOTIFICATION_EVENTS } from 'graphql/queries/notificationSettings';
import { QUERY_GET_RECORD_TYPES } from 'graphql/queries/recordTypes';
import { QUERY_GET_RECORD_FIELDS } from 'graphql/queries/bills';
import {
    MUTATION_CREATE_NOTIFICATION,
    MUTATION_UPDATE_NOTIFICATION,
    MUTATION_DELETE_NOTIFICATION,
    MUTATION_RESTORE_NOTIFICATION,
    MUTATION_TOGGLE_NOTIFICATION,
    MUTATION_BULK_TOGGLE_NOTIFICATIONS
} from 'graphql/mutations/notificationSettings';
import { Notification, NotificationFormData, RecordTypeOption, NotificationEvent, FieldOption, OperatorType } from '../types';
import { mockOperatorsByType, mockEvents } from '../mocks/notificationData';

export const useNotificationsSettings = () => {
    const [showDeleted, setShowDeleted] = useState(false);

    const {
        data: notificationsData,
        loading: loadingNotifications,
        error: notificationsError,
        refetch: refetchNotifications
    } = useQuery(QUERY_FIND_NOTIFICATIONS);

    const { data: recordTypesData, loading: loadingRecordTypes } = useQuery(QUERY_GET_RECORD_TYPES);

    const [createNotificationMutation, { loading: creating }] = useMutation(MUTATION_CREATE_NOTIFICATION, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [updateNotificationMutation, { loading: updating }] = useMutation(MUTATION_UPDATE_NOTIFICATION, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [deleteNotificationMutation, { loading: deleting }] = useMutation(MUTATION_DELETE_NOTIFICATION, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [restoreNotificationMutation, { loading: restoring }] = useMutation(MUTATION_RESTORE_NOTIFICATION, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [toggleNotificationMutation, { loading: toggling }] = useMutation(MUTATION_TOGGLE_NOTIFICATION, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [bulkToggleMutation, { loading: bulkToggling }] = useMutation(MUTATION_BULK_TOGGLE_NOTIFICATIONS, {
        refetchQueries: [{ query: QUERY_FIND_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const notifications = useMemo(() => {
        if (!notificationsData?.findNotifications) return [];
        const notifs = notificationsData.findNotifications as Notification[];
        console.log('🔍 Notifications data:', notifs);
        if (notifs.length > 0) {
            console.log('🔍 First notification recordType:', notifs[0].recordType);
        }
        return notifs;
    }, [notificationsData]);

    const recordTypes = useMemo(() => {
        if (!recordTypesData?.getRecordTypes) return [];
        return recordTypesData.getRecordTypes.map((rt: any) => ({
            id: rt.id,
            name: rt.name,
            displayName: rt.name
        })) as RecordTypeOption[];
    }, [recordTypesData]);

    const createNotification = async (data: NotificationFormData) => {
        try {
            const response = await createNotificationMutation({
                variables: { data }
            });
            return response.data?.createNotification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to create notification');
        }
    };

    const updateNotification = async (id: string, data: Partial<NotificationFormData>) => {
        try {
            const response = await updateNotificationMutation({
                variables: { id, data }
            });
            return response.data?.updateNotification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to update notification');
        }
    };

    const deleteNotification = async (id: string) => {
        try {
            await deleteNotificationMutation({
                variables: { id }
            });
        } catch (error: any) {
            throw new Error(error.message || 'Failed to delete notification');
        }
    };

    const restoreNotification = async (id: string) => {
        try {
            const response = await restoreNotificationMutation({
                variables: { id }
            });
            return response.data?.restoreNotification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to restore notification');
        }
    };

    const toggleNotification = async (id: string, enabled: boolean) => {
        try {
            const response = await toggleNotificationMutation({
                variables: { id, enabled }
            });
            return response.data?.toggleNotification;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to toggle notification');
        }
    };

    const bulkToggleNotifications = async (enabled: boolean) => {
        try {
            const response = await bulkToggleMutation({
                variables: { enabled }
            });
            return response.data?.bulkToggleNotifications;
        } catch (error: any) {
            throw new Error(error.message || 'Failed to bulk toggle notifications');
        }
    };

    return {
        notifications,
        allNotifications: notifications,
        recordTypes,
        loading: loadingNotifications || loadingRecordTypes,
        error: notificationsError,
        creating,
        updating,
        deleting,
        restoring,
        toggling,
        bulkToggling,
        showDeleted,
        setShowDeleted,
        createNotification,
        updateNotification,
        deleteNotification,
        restoreNotification,
        toggleNotification,
        bulkToggleNotifications,
        refetchNotifications
    };
};

export const useNotificationEvents = (recordTypeId: string | null) => {
    const { data, loading, error } = useQuery(QUERY_GET_NOTIFICATION_EVENTS, {
        variables: { recordTypeId: recordTypeId || '' },
        skip: !recordTypeId
    });

    console.log('🟡 [Events] Loaded for Record Type:', {
        recordTypeId,
        rawData: data?.getNotificationEvents,
        dataType: typeof data?.getNotificationEvents
    });

    const events = useMemo(() => {
        if (data?.getNotificationEvents && data.getNotificationEvents.length > 0) {
            return data.getNotificationEvents as NotificationEvent[];
        }

        if (error || !data) {
            return recordTypeId ? mockEvents[recordTypeId] || [] : [];
        }

        return [];
    }, [data, error, recordTypeId]);

    return {
        events,
        loading
    };
};

export const useNotificationFields = (recordTypeId: string | null) => {
    const { data, loading } = useQuery(QUERY_GET_RECORD_FIELDS, {
        variables: { recordType: recordTypeId ? parseFloat(recordTypeId) : 0 },
        skip: !recordTypeId,
        onCompleted: (completedData) => {
            console.log('🟡 Fields Loaded for Record Type:', {
                recordTypeId,
                rawData: completedData?.getSaasRecordFieldsByRecordTypes,
                dataType: typeof completedData?.getSaasRecordFieldsByRecordTypes
            });
        },
        onError: (error) => {
            console.error('❌ Error loading fields:', {
                recordTypeId,
                error: error.message
            });
        }
    });

    const fields = useMemo(() => {
        if (!data?.getSaasRecordFieldsByRecordTypes) return [];

        let fieldsData = data.getSaasRecordFieldsByRecordTypes;

        if (typeof fieldsData === 'string') {
            try {
                fieldsData = JSON.parse(fieldsData);
            } catch (error) {
                console.error('❌ Error parsing fields data:', error);
                return [];
            }
        }

        const fieldsList: FieldOption[] = [];
        const listFieldsCount = { total: 0, dropdown: 0, multiselect: 0 };
        const fieldsByType: Record<string, number> = {};

        Object.entries(fieldsData).forEach(([key, value]: [string, any]) => {
            if (value && typeof value === 'object') {
                const dataType = value.dataType || 'string';

                // Count fields by type
                fieldsByType[dataType] = (fieldsByType[dataType] || 0) + 1;

                // Count list fields
                if (['dropdown', 'single-select'].includes(dataType.toLowerCase())) {
                    listFieldsCount.dropdown += 1;
                    listFieldsCount.total += 1;
                } else if (['multiselect', 'multiselect-checkbox'].includes(dataType.toLowerCase())) {
                    listFieldsCount.multiselect += 1;
                    listFieldsCount.total += 1;
                }

                fieldsList.push({
                    id: value.id?.toString() || key,
                    name: key,
                    displayName: value.displayName || key,
                    dataType,
                    category: 'base',
                    listType: value.listType ? { id: value.listType.id?.toString() } : undefined
                });
            }
        });

        console.log('🟡 Fields Parsed Successfully:', {
            recordTypeId,
            totalFields: fieldsList.length,
            fieldsByType,
            listFieldsCount,
            listFields: fieldsList
                .filter((f) => f.listType)
                .map((f) => ({
                    id: f.id,
                    name: f.name,
                    dataType: f.dataType,
                    listTypeId: f.listType?.id
                })),
            sampleFields: fieldsList.slice(0, 5).map((f) => ({
                id: f.id,
                name: f.name,
                displayName: f.displayName,
                dataType: f.dataType,
                hasListType: !!f.listType
            }))
        });

        return fieldsList;
    }, [data, recordTypeId]);

    return {
        fields,
        loading
    };
};

export const useOperatorsByType = (dataType: string | null) => {
    const operators = useMemo(() => {
        if (!dataType) return [];
        const normalizedType = dataType.toLowerCase();
        return mockOperatorsByType[normalizedType] || [];
    }, [dataType]);

    return {
        operators: operators as OperatorType[],
        loading: false
    };
};
